import { HexColorPicker } from "react-colorful";
import {
  useFormContext,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

/** Curated 18-colour palette mirroring the legacy WrappedColourPicker. */
const SWATCHES = [
  "#001f3f",
  "#0074D9",
  "#7FDBFF",
  "#39CCCC",
  "#3D9970",
  "#2ECC40",
  "#01FF70",
  "#FFDC00",
  "#FF851B",
  "#FF4136",
  "#85144b",
  "#F012BE",
  "#B10DC9",
  "#111111",
  "#AAAAAA",
  "#DDDDDD",
  "#FFFFFF",
];

interface ColorPickerFieldProps<TValues extends FieldValues> {
  name: FieldPath<TValues>;
  label: string;
  description?: string;
}

export function ColorPickerField<TValues extends FieldValues>({
  name,
  label,
  description,
}: ColorPickerFieldProps<TValues>) {
  const form = useFormContext<TValues>();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => {
        const value = (field.value as string) ?? "#000000";
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-1.5 font-inter text-sm text-neutral-900 transition-colors hover:bg-neutral-50"
                  >
                    <span
                      className="inline-block h-5 w-5 rounded border border-neutral-300"
                      style={{ background: value }}
                    />
                    <span>{value}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3">
                  <HexColorPicker
                    color={value}
                    onChange={(next) => field.onChange(next)}
                  />
                  <div className="mt-3 grid grid-cols-9 gap-1">
                    {SWATCHES.map((swatch) => (
                      <button
                        key={swatch}
                        type="button"
                        className="h-5 w-5 rounded border border-neutral-300"
                        style={{ background: swatch }}
                        onClick={() => field.onChange(swatch)}
                        aria-label={swatch}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            {description ? (
              <FormDescription>{description}</FormDescription>
            ) : null}
            {fieldState.error ? (
              <FormMessage>{fieldState.error.message}</FormMessage>
            ) : null}
          </FormItem>
        );
      }}
    />
  );
}
